import {
  INTERNAL_ID_FIELDS,
  telemetryInternalIds,
} from './telemetry-identifiers';
import type { Log } from '@sentry/angular';

export const API_ROUTE_TEMPLATES = [
  '/ui-session',
  '/ui-session/:id',
  '/api/v1/conversations/:id',
  '/api/v1/conversations/:id/messages',
  '/api/v1/conversations/:id/stream',
  '/api/v1/conversations/:id/runs/:id',
  '/conversations/:id',
  '/conversations/:id/messages',
  '/conversations/:id/stream',
  '/conversations/:id/runs/:id',
  '/accounts',
  '/accounts/:type/:id',
  '/accounts/:type/:id/transactions',
  '/financialsnapshot',
  '/financialsnapshot/template',
  '/financialsnapshot/invite',
  '/credit/status',
  '/credit/report/summary',
  '/credit/report',
  '/credit/invite',
  '/v1/prototype/client/metadata',
  '/api/v1/activities',
  '/api/v1/activities/:id',
  '/api/v1/activities/:id/actions',
  '/api/v1/policy',
  '/action',
  '/action/:id',
  '/case',
  '/case/:id',
  '/case/:id/transition',
  '/case/:id/option',
  '/case/:id/option/:id',
  '/case/:id/option/:id/transition',
  '/goal',
  '/goal/:id',
  '/goal/:id/link',
  '/goal/:id/unlink',
  '/outcome',
  '/outcome/:id',
  '/outcome/:id/link',
  '/outcome/:id/unlink',
  '/outcome/:id/invalidate',
  '/unclassified',
] as const;

type LogEnvelope = readonly [
  unknown,
  Array<readonly [{ type?: string }, unknown]>,
];

export interface StructuredLogConfig {
  enabled: boolean;
  successSampleRate: number;
}

let config: StructuredLogConfig = { enabled: false, successSampleRate: 0 };

export function configureStructuredLogs(value: StructuredLogConfig): void {
  config = {
    enabled: value.enabled === true,
    successSampleRate: Math.max(0, Math.min(1, value.successSampleRate || 0)),
  };
}

export function structuredLogsEnabled(): boolean {
  return config.enabled;
}

export const LOG_TEST_SCENARIOS = [
  'action_assignment_unverified',
  'api_failed',
  'contract_mismatch',
  'action_recovered',
  'outcome_partial',
  'option_qualification_failed',
  'option_override_blocked',
  'goal_partial',
  'goal_recovered',
  'review_retry_failed',
  'send_unknown',
  'metadata_recovered',
  'credit_render_failed',

  'token_refresh_failed',
  'forced_logout',
  'connector_timeout',
  'connector_recovered',
  'permission_denied',
  'response_lost',
  'submission_failed',
  'retry_recovered',
  'navi_recovered',
  'flags_failed',
] as const;
export type LogTestScenario = (typeof LOG_TEST_SCENARIOS)[number];
export interface LogTestContext {
  source: 'logging_test';
  test_run_id: string;
  test_scenario: LogTestScenario;
}

const levels = {
  'feature.operation.failed': 'warn',
  'feature.operation.recovered': 'info',
  'api.request.failed': 'warn',
  'api.response.contract_mismatch': 'error',
  'auth.token_refresh.failed': 'warn',
  'auth.session.forced_logout': 'warn',
  'connector.connection.failed': 'warn',
  'connector.connection.recovered': 'info',
  'fa.action.failed': 'error',
  'fa.action.blocked': 'warn',
  'fa.action.outcome_unknown': 'warn',
  'fa.action.recovered': 'info',
  'copilot.submission.accepted': 'info',
  'copilot.run.reconnecting': 'warn',
  'copilot.run.recovered': 'info',
  'copilot.run.failed': 'error',
  'feature_flags.configuration_missing': 'warn',
  'feature_flags.initialization_failed': 'warn',
  'feature_flags.identification_failed': 'warn',
} as const;

export type StructuredEvent = keyof typeof levels;
export function structuredEventLevel(event: StructuredEvent) {
  return levels[event];
}

export const FA_ACTION_NAMES = [
  'care_plan.action.create',
  'care_plan.action.update',
  'care_plan.action.change_status',
  'care_plan.action.reassign',
  'care_plan.option.create',
  'care_plan.option.update',
  'care_plan.option.change_status',
  'care_plan.option.override_qualification',
  'care_plan.option.qualify',
  'care_plan.outcome.create',
  'care_plan.outcome.update',
  'care_plan.outcome.invalidate',
  'care_plan.outcome.link',
  'care_plan.outcome.unlink',
  'care_plan.outcome.reassign',
  'care_plan.goal.create',
  'care_plan.goal.update',
  'care_plan.goal.change_status',
  'care_plan.goal.link_case',
  'care_plan.goal.unlink_case',
  'care_plan.goal.save_batch',
  'care_plan.case.change_status',
  'care_plan.case.update',
  'ai_review.activity.retry',
  'financial_snapshot.send',
  'credit_overview.send',
  'care_plan.case.create',
  'ai_review.activity.confirm',
  'ai_review.activity.deny',
] as const;

const choices: Record<string, readonly string[]> = {
  method: ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'],
  route_template: API_ROUTE_TEMPLATES,
  contract_name: [
    'accounts.inventory',
    'accounts.detail',
    'accounts.transactions',
    'financial_snapshot.template',
    'financial_snapshot.snapshot',
  ],
  source: ['logging_test'],
  test_scenario: LOG_TEST_SCENARIOS,
  feature: [
    'financial_snapshot',
    'credit_overview',
    'client_metadata',
    'api',
    'care_plan',
    'ai_review',
    'copilot',
    'feature_flags',
    'authentication',
    'connectors',
  ],
  action_name: FA_ACTION_NAMES,
  actor_type: ['fa'],
  trigger: ['user', 'background'],
  stage: [
    'save',
    'refresh',
    'qualification',
    'verification',
    'update',
    'unlink',
    'link',
    'preflight',
    'load_pdfjs',
    'read_report',
    'extract_text',
    'create_canvas',
    'load_pdf',
    'load_document',
    'load_page',
    'render_page',
    'request',
    'response',
    'token_refresh',
    'sign_out',
    'connection',
    'submission',
    'create_case',
    'save_goals',
    'acceptance',
    'execution',
    'initialization',
    'identification',
  ],
  outcome: [
    'requested',
    'failed',
    'blocked',
    'unknown',
    'succeeded',
    'accepted',
    'reconnecting',
    'recovered',
  ],
  reason_code: [
    'refresh_failed',
    'invalid_token',
    'popup_blocked',
    'authorization_failed',
    'permission_denied',
    'stale_state',
    'business_rule',
    'network',
    'timeout',
    'dependency_unavailable',
    'contract_mismatch',
    'missing_configuration',
    'unknown',
  ],
};
const uuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const ids = new Set([
  'request_id',
  'attempt_id',
  'interaction_id',
  'recovery_of_attempt_id',
  'test_run_id',
]);
const numbers = new Set([
  'completed_count',
  'operation_count',
  'duration_ms',
  'attempt_number',
  'attempt_count',
  'http_status',
]);
const common = [
  ...INTERNAL_ID_FIELDS,
  'trigger',
  'request_id',
  'method',
  'route_template',
  'contract_name',
  'source',
  'test_run_id',
  'test_scenario',
  'feature',
  'stage',
  'outcome',
  'reason_code',
  'duration_ms',
  'http_status',
  'interaction_id',
];
const fa = [
  ...common,
  'completed_count',
  'operation_count',
  'actor_type',
  'trigger',
  'action_name',
  'attempt_id',
  'attempt_number',
  'recovery_of_attempt_id',
];

function attributesFor(
  event: StructuredEvent,
  attributes: Record<string, unknown>,
): Record<string, string | number> {
  const allowed = event.startsWith('fa.') ? fa : [...common, 'attempt_count'];
  const result: Record<string, string | number> = {
    event_name: event,
    app: 'brighthub',
  };
  for (const key of allowed) {
    const value = attributes[key];
    if (
      INTERNAL_ID_FIELDS.includes(key as (typeof INTERNAL_ID_FIELDS)[number])
    ) {
      const id = telemetryInternalIds({ [key]: value })[key];
      if (id) result[key] = id;
    } else if (ids.has(key) && typeof value === 'string' && uuid.test(value))
      result[key] = value;
    else if (
      numbers.has(key) &&
      typeof value === 'number' &&
      Number.isFinite(value) &&
      value >= 0 &&
      value <= 1_000_000_000
    ) {
      if (key !== 'http_status' || (Number.isInteger(value) && value <= 599))
        result[key] = value;
    } else if (typeof value === 'string' && choices[key]?.includes(value))
      result[key] = value;
  }
  for (const key of [
    'sentry.release',
    'sentry.environment',
    'sentry.sdk.name',
    'sentry.sdk.version',
  ]) {
    const value = attributes[key];
    if (typeof value === 'string' && /^[a-zA-Z0-9@._/-]{1,100}$/.test(value))
      result[key] = value;
  }
  // Authenticated internal staff context, populated by SentryService/SDK.
  const identityLimits: Record<string, number> = {
    'user.id': 256,
    'user.email': 320,
    'user.name': 256,
    'user.groups': 4096,
  };
  for (const [key, limit] of Object.entries(identityLimits)) {
    const value = attributes[key];
    if (
      typeof value === 'string' &&
      value.length > 0 &&
      value.length <= limit &&
      !Array.from(value).some(
        (character) =>
          character.charCodeAt(0) < 32 || character.charCodeAt(0) === 127,
      )
    )
      result[key] = value;
  }
  return result;
}

/** Retains approved staff identity; drops arbitrary context, content and log levels. */
export function filterStructuredLog(log: Log): Log | null {
  try {
    if (
      !config.enabled ||
      typeof log.message !== 'string' ||
      !Object.hasOwn(levels, log.message)
    )
      return null;
    const event = log.message as StructuredEvent;
    if (log.level !== levels[event]) return null;
    const attributes = attributesFor(event, log.attributes ?? {});
    if (attributes['source'] === 'logging_test') {
      if (!attributes['test_run_id'] || !attributes['test_scenario'])
        return null;
    } else {
      delete attributes['test_run_id'];
      delete attributes['test_scenario'];
    }
    if (
      event.startsWith('fa.') &&
      (!attributes['action_name'] ||
        !attributes['attempt_id'] ||
        !attributes['interaction_id'])
    )
      return null;
    if (event === 'copilot.submission.accepted') {
      const key = attributes['interaction_id'];
      if (typeof key !== 'string') return null;
      const hash = [...key].reduce(
        (value, char) => (value * 31 + char.charCodeAt(0)) >>> 0,
        0,
      );
      if (hash / 0x1_0000_0000 >= config.successSampleRate) return null;
    }
    return { ...log, attributes };
  } catch {
    return null;
  }
}

/** The SDK merges scope attributes AFTER beforeSendLog; scrub the wire payload too. */
export function filterStructuredLogEnvelope(envelope: LogEnvelope): void {
  for (let index = envelope[1].length - 1; index >= 0; index--) {
    const item = envelope[1][index];
    if (item[0].type !== 'log') continue;
    const payload = item[1] as {
      ingest_settings?: unknown;
      items?: Array<{
        body: string;
        level: Log['level'];
        attributes?: Record<string, { value: unknown; type: string }>;
      }>;
    } | null;
    if (!payload || !Array.isArray(payload.items)) {
      envelope[1].splice(index, 1);
      continue;
    }
    payload.items = payload.items.filter((entry) => {
      if (!entry || typeof entry !== 'object') return false;
      const raw = Object.fromEntries(
        Object.entries(entry.attributes ?? {}).map(([key, value]) => [
          key,
          value?.value,
        ]),
      );
      const filtered = filterStructuredLog({
        message: entry.body,
        level: entry.level,
        attributes: raw,
      });
      if (!filtered) return false;
      const safe = { ...filtered.attributes };
      entry.attributes = Object.fromEntries(
        Object.entries(safe).map(([key, value]) => [
          key,
          { value, type: typeof value === 'number' ? 'double' : 'string' },
        ]),
      );
      return true;
    });
    payload.ingest_settings = { infer_ip: 'never', infer_user_agent: 'never' };
    (item[0] as { item_count?: number }).item_count = payload.items.length;
    if (!payload.items.length) envelope[1].splice(index, 1);
  }
}
